/*
 * fm-discount-facet.js
 * ---------------------------------------------------------------------------
 * Client-side "Highest Discount" sort + "% Off" filter for collection & search.
 *
 * WHY CLIENT-SIDE: Shopify's storefront APIs cannot sort by (or facet on) a
 * computed discount %. Prefer product.metafields.custom.discount_percent
 * (Ablestar-synced); Liquid falls back to compute (fm-discount-value.liquid).
 * Stamped on each grid item as `data-fm-discount`. Non-member only.
 *
 * LARGE COLLECTIONS: when discount mode engages we:
 *   1. Filter/sort page 1 immediately (no wait)
 *   2. Stream remaining pages in the background (section JSON preferred)
 *   3. Keep the full match list in memory; render only one client page at a time
 *
 * State lives in the URL:
 *   - fm_discount : repeated/csv keys, e.g. ?fm_discount=40&fm_discount=60
 *   - fm_sort     : "discount-descending" when the Highest Discount sort is on
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__fmDiscountFacetLoaded) return;
  window.__fmDiscountFacetLoaded = true;

  var SORT_VALUE = 'discount-descending';
  var MAX_PAGES = 60;
  var FETCH_CONCURRENCY = 5;
  var DEFAULT_PAGE_SIZE_COLLECTION = 26;
  var DEFAULT_PAGE_SIZE_SEARCH = 24;

  var BUCKETS = {
    '20': { label: '20% Off', test: function (d) { return d === 20; } },
    '30': { label: '30% Off', test: function (d) { return d === 30; } },
    '40': { label: '40% Off', test: function (d) { return d === 40; } },
    '50': { label: '50% Off', test: function (d) { return d === 50; } },
    '60': { label: '60% Off', test: function (d) { return d === 60; } }
  };

  // baseUrl -> { byHandle: { handle: HTMLElement }, order: [handle], complete: bool }
  var catalogCache = {};
  var observer = null;
  var applying = false;
  var reapplyQueued = false;
  var applyGen = 0;
  var loadController = null;
  var loadBaseUrl = null;
  var loadPromise = null;
  var clientPage = 1;
  var lastFilterKey = '';
  var nativePagerDisplay = null;

  /* ------------------------------ helpers ------------------------------ */

  function getGrid() {
    return document.querySelector('.product-grid');
  }

  function getParams() {
    return new URLSearchParams(window.location.search);
  }

  function getBuckets() {
    var params = getParams();
    var raw = params.getAll('fm_discount');
    var out = [];
    raw.forEach(function (v) {
      v.split(',').forEach(function (part) {
        part = part.trim();
        if (part && BUCKETS[part] && out.indexOf(part) === -1) out.push(part);
      });
    });
    return out;
  }

  function isSortActive() {
    return getParams().get('fm_sort') === SORT_VALUE;
  }

  function isDiscountMode() {
    return isSortActive() || getBuckets().length > 0;
  }

  function discountOf(li) {
    var v = parseInt(li.getAttribute('data-fm-discount'), 10);
    return isNaN(v) ? 0 : v;
  }

  function matchesBuckets(d, buckets) {
    if (!buckets.length) return true;
    for (var i = 0; i < buckets.length; i++) {
      var b = BUCKETS[buckets[i]];
      if (b && b.test(d)) return true;
    }
    return false;
  }

  function setUrlParam(name, values) {
    var params = getParams();
    params.delete(name);
    (values || []).forEach(function (v) { params.append(name, v); });
    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    window.history.replaceState(window.history.state, '', url);
  }

  function isSearchPage() {
    return window.location.pathname.indexOf('/search') === 0;
  }

  function pageSize() {
    return isSearchPage() ? DEFAULT_PAGE_SIZE_SEARCH : DEFAULT_PAGE_SIZE_COLLECTION;
  }

  function sectionId() {
    return isSearchPage() ? 'main-search' : 'custom-collection-main';
  }

  function filterKey() {
    return getBuckets().slice().sort().join(',') + '|' + (isSortActive() ? SORT_VALUE : '');
  }

  /* --------------------------- UI syncing --------------------------- */

  function syncUI() {
    var buckets = getBuckets();
    document.querySelectorAll('input[name="fm_discount"]').forEach(function (cb) {
      cb.checked = buckets.indexOf(cb.value) !== -1;
    });
    var sortOn = isSortActive();
    document.querySelectorAll('#fm-sort-input').forEach(function (input) {
      input.value = sortOn ? SORT_VALUE : '';
    });
    document.querySelectorAll('.fm-discount-sort-option').forEach(function (a) {
      a.classList.toggle('is-active', sortOn);
    });
  }

  /* --------------------------- catalog cache --------------------------- */

  function baseUrlKey() {
    var params = getParams();
    ['page', 'sections', 'fm_discount', 'fm_sort'].forEach(function (k) { params.delete(k); });
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function ensureCache(baseUrl) {
    if (!catalogCache[baseUrl]) {
      catalogCache[baseUrl] = { byHandle: {}, order: [], complete: false };
    }
    return catalogCache[baseUrl];
  }

  function rememberItem(cache, li) {
    var handle = li.getAttribute('data-fm-handle');
    if (!handle) return false;
    if (cache.byHandle[handle]) return false;
    // Detach a clone so we can re-render pages without losing the node.
    var node = li.cloneNode(true);
    node.style.display = '';
    node.removeAttribute('data-fm-hidden');
    cache.byHandle[handle] = node;
    cache.order.push(handle);
    return true;
  }

  function seedFromGrid(baseUrl) {
    var grid = getGrid();
    var cache = ensureCache(baseUrl);
    if (!grid) return cache;
    grid.querySelectorAll('li.grid__item[data-fm-discount][data-fm-handle]').forEach(function (li) {
      rememberItem(cache, li);
    });
    return cache;
  }

  /* --------------------------- page fetching --------------------------- */

  function parseItemsFromHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var grid = doc.querySelector('.product-grid');
    if (!grid) return [];
    return Array.prototype.slice.call(
      grid.querySelectorAll('li.grid__item[data-fm-discount]')
    );
  }

  function fetchFullPageItems(baseUrl, page, signal) {
    var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
    return fetch(baseUrl + sep + 'page=' + page, {
      headers: { 'X-Requested-With': 'fetch' },
      signal: signal
    })
      .then(function (r) { return r.text(); })
      .then(function (text) { return parseItemsFromHtml(text); });
  }

  function fetchPageItems(baseUrl, page, signal) {
    var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
    var sid = sectionId();
    var sectionUrl = baseUrl + sep + 'page=' + page + '&sections=' + encodeURIComponent(sid);

    return fetch(sectionUrl, {
      headers: { 'X-Requested-With': 'fetch' },
      signal: signal
    })
      .then(function (r) {
        var ct = (r.headers.get('content-type') || '').toLowerCase();
        if (r.ok && ct.indexOf('application/json') !== -1) {
          return r.json().then(function (data) {
            var html = data && (data[sid] || (data && Object.keys(data).length ? data[Object.keys(data)[0]] : null));
            if (typeof html === 'string') return parseItemsFromHtml(html);
            return null;
          });
        }
        return null;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') throw err;
        return null;
      })
      .then(function (items) {
        if (items) return items;
        return fetchFullPageItems(baseUrl, page, signal);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') throw err;
        return [];
      });
  }

  function abortLoad() {
    if (loadController) {
      try { loadController.abort(); } catch (e) { /* ignore */ }
      loadController = null;
    }
    loadBaseUrl = null;
    loadPromise = null;
  }

  function loadRemainingPages(baseUrl, onProgress, signal) {
    var cache = ensureCache(baseUrl);
    if (cache.complete) {
      onProgress(cache);
      return Promise.resolve(cache);
    }

    function runBatch(start) {
      if (signal && signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
      if (start > MAX_PAGES) {
        cache.complete = true;
        return Promise.resolve();
      }
      var batch = [];
      for (var p = start; p < start + FETCH_CONCURRENCY && p <= MAX_PAGES; p++) batch.push(p);

      return Promise.all(batch.map(function (p) { return fetchPageItems(baseUrl, p, signal); }))
        .then(function (results) {
          var stop = false;
          results.forEach(function (items) {
            if (!items || !items.length) { stop = true; return; }
            var added = 0;
            items.forEach(function (li) {
              if (rememberItem(cache, li)) added++;
            });
            if (added === 0) stop = true;
          });
          onProgress(cache);
          if (stop) {
            cache.complete = true;
            return;
          }
          return runBatch(start + FETCH_CONCURRENCY);
        });
    }

    // Pages are 1-indexed; page 1 is already seeded from the DOM.
    return runBatch(2).then(function () {
      cache.complete = true;
      onProgress(cache);
      return cache;
    });
  }

  /* --------------------------- filter + client page --------------------------- */

  function buildFilteredList(cache) {
    var buckets = getBuckets();
    var sortOn = isSortActive();
    var list = [];
    cache.order.forEach(function (handle) {
      var li = cache.byHandle[handle];
      if (!li) return;
      if (matchesBuckets(discountOf(li), buckets)) list.push(li);
    });
    if (sortOn) {
      list.sort(function (a, b) { return discountOf(b) - discountOf(a); });
    }
    return list;
  }

  function hideNonProductTiles(grid) {
    Array.prototype.forEach.call(grid.children, function (li) {
      if (li.matches && li.matches('li.grid__item[data-fm-discount]')) return;
      if (li.classList && li.classList.contains('grid__item')) {
        li.style.display = 'none';
        li.setAttribute('data-fm-hidden', '1');
      }
    });
  }

  function renderClientPage(filtered) {
    var grid = getGrid();
    if (!grid) return 0;

    var size = pageSize();
    var totalPages = Math.max(1, Math.ceil(filtered.length / size) || 1);
    if (clientPage > totalPages) clientPage = totalPages;
    if (clientPage < 1) clientPage = 1;

    var start = (clientPage - 1) * size;
    var slice = filtered.slice(start, start + size);

    if (observer) observer.disconnect();

    // Remove current product cards; keep non-product tiles (hidden).
    Array.prototype.slice.call(grid.querySelectorAll('li.grid__item[data-fm-discount]')).forEach(function (li) {
      li.remove();
    });
    hideNonProductTiles(grid);

    slice.forEach(function (li) {
      var node = li.cloneNode(true);
      node.style.display = '';
      node.removeAttribute('data-fm-hidden');
      grid.appendChild(node);
    });

    return filtered.length;
  }

  function updateCount(n, loading) {
    document.querySelectorAll('#ViewResultsBtn').forEach(function (btn) {
      var label = 'View products (' + n + ')';
      if (loading) label += '…';
      btn.textContent = label;
    });
  }

  /* --------------------------- active chips --------------------------- */

  function injectChips() {
    var buckets = getBuckets();
    document.querySelectorAll('.fm-active-facet').forEach(function (el) { el.remove(); });
    if (!buckets.length) return;

    document.querySelectorAll('.js-active-facets-container').forEach(function (container) {
      buckets.forEach(function (key) {
        var chip = document.createElement('a');
        chip.href = '#';
        chip.className = 'active-facet-tag fm-active-facet';
        chip.setAttribute('data-fm-bucket', key);
        chip.innerHTML = BUCKETS[key].label + ' <span>&times;</span>';
        container.appendChild(chip);
      });
    });
  }

  /* --------------------------- client pagination UI --------------------------- */

  function ensureFmPagerHost() {
    var existing = document.querySelector('.fm-discount-pager');
    if (existing) return existing;

    var host = document.createElement('div');
    host.className = 'pagination-wrapper fm-discount-pager';
    host.innerHTML =
      '<nav class="pagination" role="navigation" aria-label="Pagination">' +
        '<ul class="pagination__list list-unstyled fm-discount-pager-list" role="list"></ul>' +
      '</nav>' +
      '<p class="fm-discount-loading" hidden style="text-align:center;margin:8px 0 0;font-size:13px;opacity:.7;">Loading more products…</p>';

    var native = document.querySelector('.pagination-wrapper:not(.fm-discount-pager)');
    if (native && native.parentNode) {
      native.parentNode.insertBefore(host, native.nextSibling);
    } else {
      var grid = getGrid();
      if (grid && grid.parentNode) grid.parentNode.appendChild(host);
    }
    return host;
  }

  function setNativePagerHidden(hide) {
    document.querySelectorAll('.pagination-wrapper:not(.fm-discount-pager)').forEach(function (el) {
      if (hide) {
        if (nativePagerDisplay === null) nativePagerDisplay = el.style.display;
        el.style.display = 'none';
      } else {
        el.style.display = nativePagerDisplay || '';
      }
    });
    if (!hide) nativePagerDisplay = null;
  }

  function pageWindow(current, total) {
    // Compact window around current page (match typical Shopify pager density).
    var pages = [];
    var i;
    if (total <= 7) {
      for (i = 1; i <= total; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    var start = Math.max(2, current - 1);
    var end = Math.min(total - 1, current + 1);
    if (start > 2) pages.push('…');
    for (i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('…');
    pages.push(total);
    return pages;
  }

  function renderPager(filteredCount, loading) {
    var size = pageSize();
    var totalPages = Math.max(1, Math.ceil(filteredCount / size) || 1);
    if (clientPage > totalPages) clientPage = totalPages;

    setNativePagerHidden(true);
    var host = ensureFmPagerHost();
    var list = host.querySelector('.fm-discount-pager-list');
    var loadingEl = host.querySelector('.fm-discount-loading');
    if (loadingEl) loadingEl.hidden = !loading;

    if (filteredCount === 0 && !loading) {
      host.style.display = 'none';
      return;
    }
    host.style.display = '';

    var html = '';
    if (clientPage > 1) {
      html += '<li><a href="#" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce fm-page-link" data-fm-page="' + (clientPage - 1) + '" aria-label="Previous page">‹</a></li>';
    }

    pageWindow(clientPage, totalPages).forEach(function (part) {
      if (part === '…') {
        html += '<li><span class="pagination__item">…</span></li>';
      } else if (part === clientPage) {
        html += '<li><span class="pagination__item pagination__item--current" aria-current="page">' + part + '</span></li>';
      } else {
        html += '<li><a href="#" class="pagination__item link fm-page-link" data-fm-page="' + part + '" aria-label="Page ' + part + '">' + part + '</a></li>';
      }
    });

    if (clientPage < totalPages) {
      html += '<li><a href="#" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce fm-page-link" data-fm-page="' + (clientPage + 1) + '" aria-label="Next page">›</a></li>';
    }

    list.innerHTML = html;
  }

  function removeFmPager() {
    document.querySelectorAll('.fm-discount-pager').forEach(function (el) { el.remove(); });
    setNativePagerHidden(false);
  }

  /* --------------------------- reset (non-discount) --------------------------- */

  function resetView() {
    abortLoad();
    document.querySelectorAll('[data-fm-hidden]').forEach(function (li) {
      li.style.display = '';
      li.removeAttribute('data-fm-hidden');
    });
    document.querySelectorAll('.fm-active-facet').forEach(function (el) { el.remove(); });
    removeFmPager();
    clientPage = 1;
    lastFilterKey = '';
    var grid = getGrid();
    if (grid) grid.style.opacity = '';
  }

  /* --------------------------- apply orchestration --------------------------- */

  function paintFromCache(baseUrl, loading) {
    var cache = ensureCache(baseUrl);
    var filtered = buildFilteredList(cache);
    var n = renderClientPage(filtered);
    injectChips();
    renderPager(n, loading);
    updateCount(n, loading);
    return n;
  }

  function apply() {
    if (applying) { reapplyQueued = true; return; }
    applying = true;
    var gen = ++applyGen;

    syncUI();

    if (!isDiscountMode()) {
      resetView();
      applying = false;
      if (reapplyQueued) { reapplyQueued = false; apply(); }
      return;
    }

    if (observer) observer.disconnect();

    var baseUrl = baseUrlKey();
    var fk = filterKey();
    if (fk !== lastFilterKey) {
      clientPage = 1;
      lastFilterKey = fk;
    }

    // Seed page-1 products and paint immediately.
    seedFromGrid(baseUrl);
    paintFromCache(baseUrl, !ensureCache(baseUrl).complete);

    function finish() {
      requestAnimationFrame(function () {
        observeGrid();
        applying = false;
        if (reapplyQueued) { reapplyQueued = false; apply(); }
      });
    }

    var cache = ensureCache(baseUrl);
    if (cache.complete) {
      paintFromCache(baseUrl, false);
      finish();
      return;
    }

    // Start progressive load once per baseUrl. Bucket changes only re-paint.
    if (loadBaseUrl !== baseUrl || !loadPromise) {
      abortLoad();
      loadController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      loadBaseUrl = baseUrl;
      var signal = loadController ? loadController.signal : undefined;
      loadPromise = loadRemainingPages(baseUrl, function () {
        if (!isDiscountMode()) return;
        if (baseUrlKey() !== baseUrl) return;
        paintFromCache(baseUrl, !ensureCache(baseUrl).complete);
      }, signal)
        .catch(function (e) {
          if (e && e.name === 'AbortError') return;
          if (window.console) console.warn('[fm-discount-facet]', e);
        })
        .then(function () {
          if (loadBaseUrl === baseUrl) loadPromise = null;
        });
    }

    loadPromise
      .then(function () {
        if (gen !== applyGen) return;
        if (!isDiscountMode()) return;
        paintFromCache(baseUrl, false);
      })
      .then(finish);
  }

  var applyTimer = null;
  function scheduleApply() {
    clearTimeout(applyTimer);
    applyTimer = setTimeout(apply, 60);
  }

  /* --------------------------- observers + actions --------------------------- */

  function observeGrid() {
    var grid = getGrid();
    if (!grid) return;
    if (!observer) {
      observer = new MutationObserver(function () {
        if (applying) return;
        scheduleApply();
      });
    }
    observer.observe(grid, { childList: true });
  }

  function closeSortDropdowns() {
    document.querySelectorAll('.custom_sort_container.is-open').forEach(function (c) {
      c.classList.remove('is-open');
    });
  }

  function readCheckedBucketsFromDom() {
    var out = [];
    document.querySelectorAll('input[name="fm_discount"]:checked').forEach(function (cb) {
      if (BUCKETS[cb.value] && out.indexOf(cb.value) === -1) out.push(cb.value);
    });
    return out;
  }

  function onDiscountCheckboxChange(e) {
    if (!e.target || e.target.name !== 'fm_discount') return;
    e.stopPropagation();
    // Bucket change: re-filter immediately from cache; do not abort catalog load.
    setUrlParam('fm_discount', readCheckedBucketsFromDom());
    apply();
  }

  function onClickCapture(e) {
    var pageLink = e.target.closest ? e.target.closest('.fm-page-link') : null;
    if (pageLink) {
      e.preventDefault();
      e.stopPropagation();
      var p = parseInt(pageLink.getAttribute('data-fm-page'), 10);
      if (!isNaN(p) && p >= 1) {
        clientPage = p;
        var baseUrl = baseUrlKey();
        paintFromCache(baseUrl, !ensureCache(baseUrl).complete);
        var grid = getGrid();
        if (grid && grid.scrollIntoView) {
          try { grid.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (err) { grid.scrollIntoView(true); }
        }
      }
      return;
    }

    var discSort = e.target.closest ? e.target.closest('.fm-discount-sort-option') : null;
    if (discSort) {
      e.preventDefault();
      e.stopPropagation();
      setUrlParam('fm_sort', [SORT_VALUE]);
      closeSortDropdowns();
      apply();
      return;
    }

    var realSort = e.target.closest ? e.target.closest('.sort_link, .sort-option') : null;
    if (realSort && isSortActive()) {
      setUrlParam('fm_sort', []);
      document.querySelectorAll('#fm-sort-input').forEach(function (input) { input.value = ''; });
    }

    var chip = e.target.closest ? e.target.closest('.fm-active-facet') : null;
    if (chip) {
      e.preventDefault();
      e.stopPropagation();
      var key = chip.getAttribute('data-fm-bucket');
      var buckets = getBuckets().filter(function (b) { return b !== key; });
      setUrlParam('fm_discount', buckets);
      document.querySelectorAll('input[name="fm_discount"][value="' + key + '"]').forEach(function (cb) {
        cb.checked = false;
      });
      apply();
    }
  }

  function init() {
    if (!document.querySelector('.fm-discount-filter') || !getGrid()) return;

    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('change', onDiscountCheckboxChange, true);
    window.addEventListener('popstate', scheduleApply);
    document.addEventListener('fm:collection-dom-updated', function () {
      // Shopify facet AJAX replaced the grid — drop cache for old base and re-seed.
      abortLoad();
      catalogCache = {};
      clientPage = 1;
      lastFilterKey = '';
      scheduleApply();
    });

    observeGrid();
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
